import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet, apiPostJson, apiPutJson, ApiError } from '../api/client';
import { formatMkd, todayIso } from '../lib/format';

/** Add/edit a salary payment. Ports frmRrogatAdd. */

interface StaffOption {
  id: number;
  name: string;
  roli: string | null;
  biznesi: string;
  monthlySalary: number;
}

interface Detail {
  id: number;
  sID: number | null;
  date: string;
  rbaz: number;
  rbonus: number;
  rzbritje: number;
  ravans: number;
}

export default function RrogaModal({
  variant,
  rowId,
  onClose,
  onSaved,
}: {
  variant: 'rrogat' | 'rrogatPlan';
  rowId: number | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = rowId !== null;

  const [staffId, setStaffId] = useState<number | ''>('');
  const [date, setDate] = useState(todayIso);
  const [rbaz, setRbaz] = useState('');
  const [rbonus, setRbonus] = useState('');
  const [rzbritje, setRzbritje] = useState('');
  const [ravans, setRavans] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const staffQuery = useQuery({
    queryKey: ['rrogat-staff'],
    queryFn: () => apiGet<{ staff: StaffOption[] }>('/rrogat/stafi'),
  });

  const detailQuery = useQuery({
    queryKey: ['rroga', variant, rowId],
    queryFn: () => apiGet<Detail>(`/rrogat/${variant}/rreshti/${rowId}`),
    enabled: isEdit,
  });

  useEffect(() => {
    const d = detailQuery.data;
    if (!d) return;
    setStaffId(d.sID ?? '');
    setDate(d.date.slice(0, 10));
    setRbaz(String(d.rbaz ?? ''));
    setRbonus(String(d.rbonus ?? ''));
    setRzbritje(String(d.rzbritje ?? ''));
    setRavans(String(d.ravans ?? ''));
  }, [detailQuery.data]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const staff = staffQuery.data?.staff ?? [];
  const selected = staff.find((s) => s.id === staffId);

  const num = (v: string) => (v.trim() === '' ? 0 : Number(v));
  const total = num(rbaz) + num(rbonus) + num(ravans) - num(rzbritje);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (staffId === '') return setError('Ju lutem zgjidhni një punëtor.');
    for (const [label, value] of [
      ['baza', rbaz],
      ['bonusi', rbonus],
      ['zbritja', rzbritje],
      ['avansi', ravans],
    ] as const) {
      if (value.trim() !== '' && !Number.isFinite(Number(value))) {
        return setError(`Vlera për ${label} nuk është e vlefshme.`);
      }
    }

    const body = {
      staffId,
      date,
      rbaz: num(rbaz),
      rbonus: num(rbonus),
      rzbritje: num(rzbritje),
      ravans: num(ravans),
    };

    setSaving(true);
    try {
      if (isEdit) await apiPutJson(`/rrogat/${variant}/${rowId}`, body);
      else await apiPostJson(`/rrogat/${variant}`, body);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Ruajtja dështoi. Provoni përsëri.');
    } finally {
      setSaving(false);
    }
  }

  const inputClass =
    'w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4">
      <form
        onSubmit={handleSubmit}
        className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl"
      >
        <h2 className="mb-4 text-lg font-semibold">
          {isEdit ? 'Ndrysho pagesën' : 'Shto pagesë'}
          <span className="ml-2 text-sm font-normal text-muted">
            ({variant === 'rrogat' ? 'aktuale' : 'planifikuar'})
          </span>
        </h2>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="rr-staff">
              Punëtori
            </label>
            <select
              id="rr-staff"
              value={staffId}
              onChange={(e) => setStaffId(e.target.value === '' ? '' : Number(e.target.value))}
              required
              className={`${inputClass} bg-white`}
            >
              <option value="">Zgjidhni…</option>
              {staff.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} — {s.biznesi}
                  {s.roli ? ` (${s.roli})` : ''}
                </option>
              ))}
            </select>
            {selected && (
              <p className="mt-1 text-xs text-muted">
                Rroga mujore e kontraktuar: {formatMkd(selected.monthlySalary)}
              </p>
            )}
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="rr-date">
              Data
            </label>
            <input
              id="rr-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              className={inputClass}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="rr-baz">
              Baza
            </label>
            <input
              id="rr-baz"
              inputMode="decimal"
              value={rbaz}
              onChange={(e) => setRbaz(e.target.value.replace(/[^\d.]/g, ''))}
              className={`tnum ${inputClass}`}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="rr-bonus">
              Bonusi
            </label>
            <input
              id="rr-bonus"
              inputMode="decimal"
              value={rbonus}
              onChange={(e) => setRbonus(e.target.value.replace(/[^\d.]/g, ''))}
              className={`tnum ${inputClass}`}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="rr-zbritje">
              Zbritja
            </label>
            <input
              id="rr-zbritje"
              inputMode="decimal"
              value={rzbritje}
              onChange={(e) => setRzbritje(e.target.value.replace(/[^\d.]/g, ''))}
              className={`tnum ${inputClass}`}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="rr-avans">
              Avansi
            </label>
            <input
              id="rr-avans"
              inputMode="decimal"
              value={ravans}
              onChange={(e) => setRavans(e.target.value.replace(/[^\d.]/g, ''))}
              className={`tnum ${inputClass}`}
            />
          </div>
        </div>

        {/* Running total, so the arithmetic is visible before saving. */}
        <div className="mt-4 flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
          <span className="text-sm text-muted">Totali (bazë + bonus + avans − zbritje)</span>
          <span className="tnum font-semibold">{formatMkd(total)}</span>
        </div>

        {error && (
          <div role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Anulo
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-brand-500 px-5 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60"
          >
            {saving ? 'Duke ruajtur…' : 'Ruaj'}
          </button>
        </div>
      </form>
    </div>
  );
}
